/**
 * Event Batcher
 * Batches detection samples and sends to ingest API
 */

import type { DetectionSample } from '@reasoncode/types/detection';
import { computeHMAC } from './hmac';
import { getSessionId } from './session';

export class Batcher {
  private queue: DetectionSample[] = [];
  private timer: number | null = null;
  private flushing = false;

  constructor(
    private endpoint: string,
    private projectId: string,
    private apiKey: string,
    private interval: number,
    private maxSize: number,
    private debug: boolean
  ) {}

  /**
   * Add a sample to the queue
   */
  add(sample: DetectionSample): void {
    this.queue.push(sample);

    if (this.queue.length >= this.maxSize) {
      this.flush();
    } else if (!this.timer) {
      this.scheduleFlush();
    }
  }

  /**
   * Flush queue asynchronously
   */
  async flush(): Promise<void> {
    if (this.flushing || this.queue.length === 0) return;

    this.flushing = true;
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;

    const batch = this.queue.splice(0, this.maxSize);
    const payload = JSON.stringify({ batch });

    try {
      const mac = await computeHMAC(this.projectId, payload, this.apiKey);

      const response = await fetch(`${this.endpoint}/v1/ingest/detection`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-RC-PROJECT': this.projectId,
          'X-RC-MAC': `sha256=${mac}`,
          'X-RC-SESSION': getSessionId(),
        },
        body: payload,
        keepalive: true,
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown error');
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      if (this.debug) {
        console.log(`[ReasonCode] Flushed ${batch.length} events`);
      }
    } catch (err) {
      // Put batch back in queue for retry
      this.queue.unshift(...batch);

      if (this.debug) {
        console.error('[ReasonCode] Flush failed:', err);
      }
    } finally {
      this.flushing = false;
      this.scheduleFlush();
    }
  }

  /**
   * Synchronous flush using sendBeacon (for page unload)
   */
  flushSync(): void {
    if (this.queue.length === 0) return;

    const batch = this.queue.splice(0, this.maxSize);
    const payload = JSON.stringify({ batch });

    // Use sendBeacon for guaranteed delivery on page unload
    const blob = new Blob([payload], { type: 'application/json' });
    const success = navigator.sendBeacon(`${this.endpoint}/v1/ingest/detection`, blob);

    if (this.debug) {
      console.log(`[ReasonCode] sendBeacon: ${success ? 'queued' : 'failed'} for ${batch.length} events`);
    }

    // Clear remaining queue
    this.queue = [];
  }

  /**
   * Schedule next flush
   */
  private scheduleFlush(): void {
    if (this.timer) return;
    if (typeof window === 'undefined') return;

    this.timer = window.setTimeout(() => this.flush(), this.interval);
  }
}
